import type { PreferenceValues } from '../contract/types';
import { numberFormat } from './intl-cache';
import { currencyCodeFor } from './money';

/**
 * The pieces of a formatted amount, for a field the reader types into.
 *
 * Everything that merely shows an amount wants `formatMoney`, which assembles these itself. An
 * input cannot: the digits belong to the field and the symbol sits beside it, so the two are laid
 * out separately and the layout has to be asked for rather than assumed. Assuming it is how a
 * leading `$` comes to sit in front of a Quebec reader's field, where the currency trails.
 */

export type CurrencyFacts = {
  /** The code the amount is in. Blank when the caller did not name a supported one. */
  readonly code: string;
  /** The symbol this locale writes that code as: `$`, `$US`, `€`. */
  readonly symbol: string;
  /** Which side of the digits the symbol sits on. */
  readonly placement: 'before' | 'after';
  /** What the locale puts between the two, which is not always nothing. */
  readonly separator: string;
  /**
   * Fractional digits the currency carries. Two for `SUPPORTED_CURRENCY_CODES`.
   * Absent when the facts are blank. Zero is a real width — a currency with no
   * fractional unit — so it cannot stand in for a code the caller did not name.
   */
  readonly digits: number | undefined;
};

/** Which currency to describe. */
export type CurrencyFactsOptions = {
  /**
   * ISO 4217 code for this particular field, when it carries one.
   *
   * Omit it and the facts are blank. There is no digit count to give
   * here as there is on `formatMoney`: how many decimals the currency carries is one of the facts
   * being asked for, so overriding it would answer the question with the question.
   */
  readonly code?: string;
};

/**
 * How many currencies are described at once.
 *
 * One reader's session touches a handful — their own, plus whatever codes their rows carry — so the
 * bound is far above a real working set, and a caller sweeping codes degrades to the uncached cost
 * rather than growing the heap for the life of the tab.
 */
const CURRENCY_CACHE_LIMIT = 100;

const BLANK_CURRENCY_FACTS: CurrencyFacts = {
  code: '',
  symbol: '',
  placement: 'before',
  separator: '',
  digits: undefined,
};

const described = new Map<string, CurrencyFacts>();

/** Empties the cache. For tests that change what ICU answers between cases. */
export const resetCurrencyCache = (): void => {
  described.clear();
};

/** The part types a number is built out of, as against the symbol and the spacing around it. */
const NUMERIC_PARTS = new Set([
  'integer',
  'group',
  'decimal',
  'fraction',
  'minusSign',
  'plusSign',
]);

/**
 * What a currency looks like to this reader: its symbol, which side that symbol sits on, what
 * separates the two, and how many decimals it carries.
 *
 * Read back out of the same formatter that renders the amounts, as a unit label is, so a field's
 * adornment and the `<Money>` beside it cannot come to disagree — even after an ICU upgrade moves
 * one of them.
 *
 * @param preferences - Supplies the locale.
 * @param options - An explicit currency, for a field denominated in something else.
 * @returns The four facts, with the code they describe.
 */
export const currencyFactsOf = (
  preferences: PreferenceValues,
  options?: CurrencyFactsOptions
): CurrencyFacts => {
  const code = currencyCodeFor(options?.code, 'currencyFactsOf.code');
  if (code === undefined) return BLANK_CURRENCY_FACTS;

  // A null character cannot appear in a language tag, so no locale can collide with the separator.
  const key = `${preferences.locale}\u0000${code}`;
  const hit = described.get(key);
  if (hit !== undefined) return hit;

  const formatter = numberFormat(preferences.locale, {
    style: 'currency',
    currency: code,
  });
  const parts = formatter.formatToParts(0);
  const symbolAt = parts.findIndex(({ type }) => type === 'currency');
  const digitsAt = parts.findIndex(({ type }) => NUMERIC_PARTS.has(type));
  const placement = symbolAt < digitsAt ? 'before' : 'after';
  // Whatever lies between the two, which is a narrow no-break space in some locales, a plain one in
  // others and nothing at all in en-US.
  const separator = parts
    .slice(Math.min(symbolAt, digitsAt) + 1, Math.max(symbolAt, digitsAt))
    .filter(({ type }) => type === 'literal')
    .map(({ value }) => value)
    .join('');

  const facts: CurrencyFacts = {
    code,
    // A join of one in every case ICU currently produces, written as a join anyway because nothing
    // in the specification promises a symbol arrives as a single part.
    symbol: parts
      .filter(({ type }) => type === 'currency')
      .map(({ value }) => value)
      .join(''),
    placement,
    separator,
    // The currency's own convention, which is what `formatMoney` renders by when no caller overrides
    // it. A field that allowed finer input than this would submit a figure money cannot hold.
    //
    // Counted off the zero just formatted rather than read from `resolvedOptions`, so it comes from
    // the same string every other member here does: two digits where the amount reads `$0.00`, none
    // where it reads `￥0`, three for a currency carrying a thousandth part.
    digits: parts.find(({ type }) => type === 'fraction')?.value.length ?? 0,
  };

  // Cleared wholesale rather than evicted one at a time: past the bound the working set has already
  // been left behind, and a description costs little enough to rebuild that tracking recency would
  // not pay for itself.
  if (described.size >= CURRENCY_CACHE_LIMIT) described.clear();

  described.set(key, facts);
  return facts;
};
